import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Spin } from 'antd';
import axios from 'axios';
import './locationSelection.scss'

/**
 * LocationSelection Component
 * 
 * This component provides location search functionality with the following features:
 * - Automatically bypasses global loader (configured in api_helper.js interceptors)
 * - Shows local loading indicator (spinner) positioned near the input field
 * - Debounced search to prevent excessive API calls
 * - Uses standard axios calls (global loader is automatically skipped for location APIs)
 * - Dropdown with search results
 * - Outside click handling to close dropdown
 * 
 * @param {Function} errorHandle - Callback for error handling
 * @param {boolean} disableStatus - Whether the input should be disabled
 * @param {string} apiUrl - API endpoint for location search
 * @param {string} placeholder - Input placeholder text
 * @param {Function} selectedLocation - Callback when location is selected
 * @param {string} value - Initial/controlled input value
 */
const LocationSelection = ({ errorHandle, disableStatus, apiUrl, placeholder, selectedLocation, value, externalLoading = false }) => {
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);
    const isInitialized = useRef(false);
    
    // State management
    const [dropdownVisibility, setDropdownVisibility] = useState(false);
    const [searchResult, setSearchResult] = useState([]);
    const [error, setError] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);

    // Location API call function - now uses standard axios (global loader is automatically skipped)
    const makeLocationApiCall = useCallback(async (url, method = 'GET', data = null) => {
        try {
            console.log('🌐 Making location API call:', { url, method, data });
            
            let response;
            if (method.toLowerCase() === 'get') {
                if (data) {
                    response = await axios.get(url, { params: data });
                } else {
                    response = await axios.get(url);
                }
            } else if (method.toLowerCase() === 'post') {
                response = await axios.post(url, data);
            }
            
            console.log('✅ Location API response received:', response);
            return response;
        } catch (error) {
            console.error('❌ Location API call failed:', error);
            throw error;
        }
    }, []);

    // Memoized function to map API response to options
    const mapLocationResponseToOptions = useCallback((data) => {
        try {
            console.log('🗺️ Mapping location response data:', data);
            
            if (!data) {
                console.warn('⚠️ No data received from API');
                return [];
            }

            // Handle different possible response structures
            let locationData = null;
            
            if (data.location_data) {
                // Structure: { location_data: { result: [...] } } or { location_data: [...] }
                locationData = data.location_data.result || data.location_data;
                console.log('📍 Found location_data structure:', locationData);
            } else if (data.data && data.data.location_data) {
                // Structure: { data: { location_data: { result: [...] } } }
                locationData = data.data.location_data.result || data.data.location_data;
                console.log('📍 Found nested data.location_data structure:', locationData);
            } else if (Array.isArray(data)) {
                // Direct array response
                locationData = data;
                console.log('📍 Found direct array structure:', locationData);
            } else if (data.result && Array.isArray(data.result)) {
                // Structure: { result: [...] }
                locationData = data.result;
                console.log('📍 Found result array structure:', locationData);
            } else {
                console.warn('⚠️ Unknown API response structure:', data);
                return [];
            }
            
            if (!Array.isArray(locationData)) {
                console.warn('⚠️ Location data is not an array:', locationData);
                return [];
            }

            console.log(`🔢 Processing ${locationData.length} location items`);

            const mappedLocations = locationData.map((location, index) => {
                // Ensure ID is a valid 24-character ObjectID or create a fallback
                let locationId = location.id || `location_${index}`;
                if (locationId.length !== 24) {
                    // If the ID is not 24 characters, pad or truncate to make it 24 characters
                    if (locationId.length < 24) {
                        locationId = locationId.padEnd(24, '0');
                    } else {
                        locationId = locationId.substring(0, 24);
                    }
                }
                
                const mappedLocation = {
                    id: locationId,
                    label: location.display_name || location.name || '',
                    value: location.display_name || location.name || '',
                    lat: location.lat,
                    lon: location.lon,
                    display_place: location.display_place,
                    display_address: location.display_address,
                    name: location.name,
                    county: location.county,
                    zipcode: location.zipcode,
                    country: location.country,
                    state: location.state,
                    city: location.city,
                    geojson: location.geojson
                };
                
                console.log(`📍 Mapped location ${index + 1}:`, mappedLocation);
                return mappedLocation;
            });

            console.log('✅ Successfully mapped all locations:', mappedLocations);
            return mappedLocations;
        } catch (error) {
            console.error('❌ Error mapping location response:', error);
            return [];
        }
    }, []);

    // Debounced search function
    const performSearch = useCallback(async (searchValue) => {
        // Clear any existing timeout
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        // Don't search for empty values
        if (!searchValue || !searchValue.trim()) {
            setSearchResult([]);
            setError(false);
            setLoading(false);
            setDropdownVisibility(false);
            return;
        }

        setLoading(true);
        setError(false);
        setDropdownVisibility(true);

        try {
            console.log('🔍 Searching for location:', searchValue);
            console.log('🔗 API URL:', apiUrl);

            let response;
            try {
                // Try GET with query parameter first (global loader automatically skipped)
                response = await makeLocationApiCall(`${apiUrl}?q=${encodeURIComponent(searchValue)}`, 'GET');
                console.log('📍 Location API response (GET):', response);
            } catch (getError) {
                console.log('⚠️ GET request failed, trying POST:', getError);
                // Fallback to POST with body (global loader automatically skipped)
                response = await makeLocationApiCall(apiUrl, 'POST', { location: searchValue });
                console.log('📍 Location API response (POST):', response);
            }
            
            
            if (response) {
                const options = mapLocationResponseToOptions(response);
                console.log('✅ Mapped location options:', options);
                console.log('🔢 Number of options found:', options.length);
                
                setSearchResult(options);
                setError(false);
                if (errorHandle) errorHandle(false);
            } else {
                console.warn('⚠️ Empty API response');
                setSearchResult([]);
                setError(true);
                if (errorHandle) errorHandle(true);
            }
        } catch (error) {
            console.error('❌ Location search error:', error);
            console.error('❌ Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });
            setSearchResult([]);
            setError(true);
            if (errorHandle) errorHandle(true);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, mapLocationResponseToOptions, errorHandle, makeLocationApiCall]);

    // Handle input change with debouncing
    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setError(false);
        
        // Clear any existing timeout
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        // If input is empty, clear results immediately
        if (!newValue.trim()) {
            setSearchResult([]);
            setDropdownVisibility(false);
            setLoading(false);
            return;
        }

        // Set new timeout for debounced search
        debounceRef.current = setTimeout(() => {
            performSearch(newValue);
        }, 300);
    }, [performSearch]);

    // Handle location selection
    const handleLocationSelection = useCallback((selectedOption) => {
        console.log('📍 Location selected:', selectedOption);
        
        // Clear any pending search
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        
        // Update states immediately
        setInputValue(selectedOption.display_place || selectedOption.name || selectedOption.label);
        setDropdownVisibility(false);
        setLoading(false);
        setError(false);
        setSearchResult([]);
        
        // Call parent callback
        if (selectedLocation) {
            selectedLocation(selectedOption);
        }
    }, [selectedLocation]);

    // Handle outside click to close dropdown
    const handleOutsideClick = useCallback((event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setDropdownVisibility(false);
            setLoading(false);
        }
    }, []);

    // Initialize value from props
    useEffect(() => {
        if (value && value !== inputValue && !isInitialized.current) {
            setInputValue(value);
            isInitialized.current = true;
        }
    }, [value, inputValue]);

    // Set up outside click listener
    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [handleOutsideClick]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="location_selection_wrap" ref={dropdownRef}>
            <div className="location_input">
                <input 
                    type="text" 
                    className='input_field' 
                    onChange={handleChange} 
                    value={inputValue} 
                    placeholder={placeholder}
                    disabled={disableStatus}
                />
                {(loading || externalLoading) && (
                    <div className="location_loading_indicator">
                        <Spin size="small" />
                        <span className="loading_text">Searching...</span>
                    </div>
                )}
            </div>
            
            {error && (
                <div className="error_message" style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    Failed to load locations. Please try again.
                </div>
            )}
            
            {dropdownVisibility && (
                <div className="location_listing">
                    {searchResult.length > 0 ? (
                        <ul>
                            {searchResult.map((location) => (
                                <li
                                    key={location.id}
                                    className='text_sm'
                                    onClick={() => handleLocationSelection(location)}
                                >
                                    <div>
                                        <div style={{ fontWeight: '500' }}>{location.display_place || location.name}</div>
                                        {location.display_address && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {location.display_address}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                            {loading ? 'Searching...' : 'No locations found'}
                        </div>
                    )}
                </div>
            )}
            
        </div>
    );
};

export default LocationSelection